import { useTranslation } from 'react-i18next';
import { type ListSigningDto } from 'xosign-types';
import classNames from 'classnames';
import Skeleton from '@/components/Skeleton';
import ListItem from '../ListItem';
import ListItemEmpty from '../ListItemEmpty';
import IcCirclePartial from '@/assets/icons/CirclePartial.svg?react';
import styles from './Listing.module.scss';

interface ListingProps {
  className?: string;
  deletingIds?: string[];
  hasSearchTerm?: boolean;
  label: string;
  loading: boolean | 'update';
  loadSignings?: () => void;
  onDelete?: (signing: ListSigningDto) => void;
  signings: ListSigningDto[];
  type:
    | 'all'
    | 'draft'
    | 'pending'
    | 'done'
    | 'expired'
    | 'cancelled'
    | 'multi';
}

const Listing = ({
  className,
  deletingIds = [],
  hasSearchTerm = false,
  label,
  loading,
  onDelete,
  signings,
  type,
}: ListingProps) => {
  const { t } = useTranslation(['signings']);

  const isEmpty = signings.length === 0 && !loading;
  const showRichEmpty = isEmpty && !hasSearchTerm;
  const showNoResults = isEmpty && hasSearchTerm;

  return (
    <section className={classNames(styles.section, className)}>
      <h2 className={styles.title}>
        {label} <span className={styles.indicator}>{signings.length}</span>
        {loading && <IcCirclePartial className={styles.loading} />}
      </h2>

      {showNoResults && (
        <div className={styles.noResults}>{t('signings:empty.noResults')}</div>
      )}

      {showRichEmpty && <ListItemEmpty type={type} />}

      {(!isEmpty || loading) && (
        <ol className={styles.list}>
          {signings.length === 0 && loading && (
            <Skeleton className={styles.skeleton} count={2} tag="li" />
          )}
          {signings.map((signing) => (
            <ListItem
              key={signing.id}
              signing={signing}
              isLoading={loading === true}
              isDeleting={deletingIds.includes(signing.id)}
              onDelete={onDelete}
            />
          ))}
        </ol>
      )}
    </section>
  );
};

export default Listing;
